import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

function AdminEditProfilePage() {
  const navigate = useNavigate();
  const { userId } = useParams();

  // 🚀 BOLD: State for tracking multiple profiles
  const [allUserProfiles, setAllUserProfiles] = useState([]);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  // 🚀 NEW: Track which profile ID is currently selected for editing
  const [selectedProfileId, setSelectedProfileId] = useState(null);

  const [formData, setFormData] = useState({
    college: "",
    display_name: "", // Alias name state
    branch: "Not Set",
    year: "Not Set",
    bio: "",
    price_per_session: 0,
    session_duration_minutes: 20,
  });

  const [loading, setLoading] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [currentAvatar, setCurrentAvatar] = useState(
    "https://via.placeholder.com/100"
  );
  const [idCardFile, setIdCardFile] = useState(null);
  const [currentIdCard, setCurrentIdCard] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [allColleges, setAllColleges] = useState([]);

  // 🚀 BOLD: Function to populate form when a specific college profile is clicked
  const populateForm = (profile) => {
    setSelectedProfileId(profile._id); // Keep track of current ID
    setFormData({
      college: profile.college ? profile.college._id : "",
      display_name: profile.display_name || "",
      branch: profile.branch || "Not Set",
      year: profile.year || "Not Set",
      bio: profile.bio || "",
      price_per_session: profile.price_per_session || 0,
      session_duration_minutes: profile.session_duration_minutes || 20,
    });
    setCurrentAvatar(profile.avatar || "https://via.placeholder.com/100");
    if (profile.tags) {
      setSelectedTags(profile.tags.map((t) => t._id));
    } else {
      setSelectedTags([]);
    }
    setCurrentIdCard(profile.id_card_url || null);
    setIsCreatingNew(false);
  };

  // 🚀 BOLD: Function to reset form for a new college profile
  const handleAddNew = () => {
    setSelectedProfileId(null);
    setFormData({
      college: "",
      display_name: "",
      branch: "Not Set",
      year: "Not Set",
      bio: "",
      price_per_session: 0,
      session_duration_minutes: 20,
    });
    setCurrentAvatar("https://via.placeholder.com/100");
    setSelectedTags([]);
    setCurrentIdCard(null);
    setImageFile(null);
    setIdCardFile(null);
    setIsCreatingNew(true);
  };

  // 🔥 NEW: DELETE PROFILE FUNCTION
  const handleDeleteProfile = async (profileId, collegeName) => {
    // 1. Ask for confirmation
    if (!window.confirm(`Are you sure you want to delete the profile for "${collegeName || 'Unknown College'}"? This cannot be undone.`)) {
      return;
    }

    const toastId = toast.loading("Deleting profile...");
    try {
      const token = localStorage.getItem("token");
      // 2. Call Backend API
      await axios.delete(
        `https://collegeconnect-backend-mrkz.onrender.com/api/profile/${profileId}`,
        { headers: { "x-auth-token": token } }
      );

      toast.dismiss(toastId);
      toast.success(`Profile for ${collegeName} deleted! 🗑️`);

      // 3. Update local state to remove the deleted profile from tabs immediately
      const updatedProfiles = allUserProfiles.filter(p => p._id !== profileId);
      setAllUserProfiles(updatedProfiles);

      // 4. If we deleted the currently viewed profile, reset the form
      if (selectedProfileId === profileId) {
        if (updatedProfiles.length > 0) {
          populateForm(updatedProfiles[0]);
        } else {
          handleAddNew();
        }
      }

    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err.response?.data?.msg || "Error deleting profile!");
    }
  };


  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setLoading(false);
          toast.error("No token found.");
          return;
        }

        // Fetch Tags and Colleges parallelly
        const tagsRes = await axios.get(
          "https://collegeconnect-backend-mrkz.onrender.com/api/tags",
          { headers: { "x-auth-token": token } }
        );
        setAllTags(tagsRes.data);

        const collegesRes = await axios.get(
          "https://collegeconnect-backend-mrkz.onrender.com/api/colleges",
          { headers: { "x-auth-token": token } }
        );
        setAllColleges(collegesRes.data);

        // Fetch user profiles
        try {
          const profileRes = await axios.get(
            `https://collegeconnect-backend-mrkz.onrender.com/api/profile/user/${userId}`,
            { headers: { "x-auth-token": token } }
          );

          // 🚀 BOLD: Ab data array mein aayega
          const profiles = profileRes.data;
          if (profiles && profiles.length > 0) {
            setAllUserProfiles(profiles);
            populateForm(profiles[0]); // By default pehla profile load karein
          } else {
            handleAddNew();
          }
        } catch (profileErr) {
          // Agar profile nahi milti (404), to naya create karne ka option dein
          if (profileErr.response && profileErr.response.status === 404) {
            setAllUserProfiles([]);
            handleAddNew();
          } else {
            throw profileErr; // Agar koi aur error hai to catch block me bhej dein
          }
        }

        setLoading(false);
      } catch (err) {
        toast.error("Failed to load data");
        setLoading(false);
      }
    };
    loadData();
  }, [userId]);

  const onChangeHandler = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });
  const onFileChangeHandler = (e) => setImageFile(e.target.files[0]);
  const onIdCardFileChange = (e) => setIdCardFile(e.target.files[0]);
  const onTagChangeHandler = (e) => {
    const tagId = e.target.value;
    if (e.target.checked)
      setSelectedTags([...selectedTags, tagId]);
    else setSelectedTags(selectedTags.filter((id) => id !== tagId));
  };

  const onSubmitHandler = async (e) => {
    e.preventDefault();
    if (!formData.college) {
      toast.error("Please select a college!");
      return;
    }

    const toastId = toast.loading("Saving profile...");
    const data = new FormData();
    data.append("college", formData.college);
    data.append("display_name", formData.display_name);
    data.append("branch", formData.branch);
    data.append("year", formData.year);
    data.append("bio", formData.bio);
    data.append("price_per_session", formData.price_per_session);
    data.append("session_duration_minutes", formData.session_duration_minutes);
    data.append("tags", selectedTags.join(","));
    if (imageFile) data.append("image", imageFile);
    if (idCardFile) data.append("id_card", idCardFile);

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `https://collegeconnect-backend-mrkz.onrender.com/api/profile/admin/${userId}`,
        data,
        {
          headers: {
            "x-auth-token": token,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.dismiss(toastId);
      toast.success("Profile Saved Successfully!");
      // Refresh list after save
       const profileRes = await axios.get(
            `https://collegeconnect-backend-mrkz.onrender.com/api/profile/user/${userId}`,
            { headers: { "x-auth-token": token } }
          );
        setAllUserProfiles(profileRes.data);
         // Find the newly saved profile to keep it selected
        const savedProfile = profileRes.data.find(p => p.college?._id === formData.college);
        if(savedProfile) populateForm(savedProfile);

    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Error saving profile!");
    }
  };

  if (loading)
    return (
      <div style={{ textAlign: "center", marginTop: "50px", color: "#2563eb" }}>
        <h2>⚙️ Loading Profile...</h2>
      </div>
    );

  return (
    <div style={mainWrapper}>
      <form style={formBox} onSubmit={onSubmitHandler}>
        <h2 style={title}>🧩 Manage Senior Profiles</h2>

        {/* 🚀 BOLD: Profile Navigation Tabs with Delete option */}
        <div style={tabsWrapper}>
          {allUserProfiles.length === 0 && !isCreatingNew && (
             <p style={{color: '#666', fontSize: '0.9rem', width: '100%', textAlign: 'center'}}>No profiles found. Click 'Add New' to start.</p>
          )}
          {allUserProfiles.map((p, idx) => {
             const isActive = formData.college === p.college?._id && !isCreatingNew;
             return (
              <div key={p._id} style={tabContainerStyle(isActive)}>
                 {/* Tab Button to load profile */}
                <button
                  type="button"
                  onClick={() => populateForm(p)}
                  style={tabStyle(isActive)}
                >
                  🏫 {p.college?.name || "Unknown College"}
                  {p.display_name && <span style={{fontSize:'0.75em', display:'block', opacity: 0.8}}>({p.display_name})</span>}
                </button>
                 {/* 🔥 Delete Button */}
                <button 
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDeleteProfile(p._id, p.college?.name); }}
                    style={deleteButtonStyle(isActive)}
                    title="Delete this profile"
                >
                  🗑️
                </button>
              </div>
            );
          })}

          <button 
            type="button" 
            onClick={handleAddNew} 
            style={tabStyleAdd(isCreatingNew)}
          >
            ➕ Add New College
          </button>
        </div>

        {/* Profile Avatar */}
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <img src={currentAvatar} alt="Avatar" style={avatarStyle} />
          <label style={label}>Change Profile Photo</label>
          <input type="file" onChange={onFileChangeHandler} style={fileInput} />
        </div>

        {/* ID Card Upload */}
        <div style={sectionBox}>
          <label style={label}>Upload ID Card (for verification)</label>
          <input type="file" onChange={onIdCardFileChange} style={fileInput} />
          {currentIdCard && !isCreatingNew && (
            <a
              href={currentIdCard}
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              View Uploaded ID Card 🔗
            </a>
          )}
        </div>

        {/* College Dropdown */}
        <div style={formGroup}>
          <label style={label}>Select College</label>
          <select
            name="college"
            value={formData.college}
            onChange={onChangeHandler}
            style={dropdown}
            disabled={!isCreatingNew} // 🚀 BOLD: Agar edit kar rahe hain to college disable rakhein
          >
            <option value="">Select a College</option>
            {allColleges.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
          {!isCreatingNew && (
            <small style={{ color: "#ef4444", marginTop: "5px" }}>
              * To change college, delete this profile and create a new one.
            </small>
          )}
        </div>

        {/* 🚀 NEW: Display Name (Alias) Input */}
        <div style={formGroup}>
          <label style={label}>Display Name (For Students)</label>
          <input
            type="text"
            name="display_name"
            value={formData.display_name}
            onChange={onChangeHandler}
            placeholder="e.g. Rahul (Leave blank to use original)"
            style={input}
          />
          <small style={{ color: "#6b7280", marginTop: "4px", fontSize: "0.85rem" }}>
            *If left blank, the senior's original user name will be shown.
          </small>
        </div>

        {/* Branch Input */}
        <div style={formGroup}>
          <label style={label}>Branch</label>
          <input
            type="text"
            name="branch"
            value={formData.branch}
            onChange={onChangeHandler}
            style={input}
          />
        </div>

        {/* Year Dropdown */}
        <div style={formGroup}>
          <label style={label}>Current Year / Status</label>
          <select
            name="year"
            value={formData.year}
            onChange={onChangeHandler}
            style={dropdown}
          >
            <option value="Not Set">Select Status</option>
            <option value="1st Year">1st Year</option>
            <option value="2nd Year">2nd Year</option>
            <option value="3rd Year">3rd Year</option>
            <option value="4th Year">4th Year</option>
            <option value="Placed in Company">Placed in Company</option>
          </select>
        </div>

        {/* Bio */}
        <div style={formGroup}>
          <label style={label}>Short Bio</label>
          <textarea
            name="bio"
            value={formData.bio}
            onChange={onChangeHandler}
            rows="3"
            style={textarea}
          />
        </div>

        {/* Tags */}
        <div style={formGroup}>
          <label style={label}>Specializations</label>
          <div style={tagGrid}>
            {allTags.map((tag) => (
              <label key={tag._id} style={tagLabel}>
                <input
                  type="checkbox"
                  value={tag._id}
                  checked={selectedTags.includes(tag._id)}
                  onChange={onTagChangeHandler}
                  style={{ marginRight: "6px" }}
                />
                {tag.name}
              </label>
            ))}
          </div>
        </div>

        {/* Price & Duration */}
        <h3 style={subTitle}>💰 Session Settings</h3>
        <div style={formGroup}>
          <label style={label}>Price (₹)</label>
          <input
            type="number"
            name="price_per_session"
            value={formData.price_per_session}
            onChange={onChangeHandler}
            style={input}
          />
        </div>
        <div style={formGroup}>
          <label style={label}>Duration (minutes)</label>
          <input
            type="number"
            name="session_duration_minutes"
            value={formData.session_duration_minutes}
            onChange={onChangeHandler}
            style={input}
          />
        </div>

        <button type="submit" style={btnGradient}>
          💾 {isCreatingNew ? "Create Profile" : "Save Changes"}
        </button>
      </form>
    </div>
  );
}

/* 🎨 Inline Styles */
const mainWrapper = {
  minHeight: "100vh",
  background: "linear-gradient(135deg,#eef2ff,#f9fafb)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "40px 10px",
};

const formBox = {
  background: "rgba(255,255,255,0.9)",
  backdropFilter: "blur(8px)",
  borderRadius: "16px",
  boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
  padding: "25px",
  width: "100%",
  maxWidth: "500px",
  animation: "fadeIn 0.6s ease",
};

const title = {
  textAlign: "center",
  color: "#2563eb",
  fontWeight: 700,
  marginBottom: "15px",
};

// 🚀 BOLD: Tabs Styles
const tabsWrapper = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  marginBottom: "20px",
  padding: "10px",
  background: "#f1f5f9",
  borderRadius: "12px",
  justifyContent: "center"
};

// 🔥 NEW: Container for Tab + Delete Button
const tabContainerStyle = (isActive) => ({
  display: 'flex',
  alignItems: 'center',
  background: isActive ? "#2563eb" : "#fff",
  border: isActive ? "none" : "1px solid #cbd5e1",
  borderRadius: "8px",
  paddingRight: '5px', // space for delete button
  transition: "all 0.2s",
});

const tabStyle = (isActive) => ({
  background: 'transparent',
  color: isActive ? "#fff" : "#334155",
  border: "none",
  padding: "6px 12px",
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
  textAlign: 'left',
});

// 🔥 NEW: Delete Button Style
const deleteButtonStyle = (isActive) => ({
  background: 'transparent',
  border: 'none',
  color: isActive ? '#ffcccc' : '#ef4444', // Lighter red if active background
  cursor: 'pointer',
  fontSize: '1rem',
  padding: '4px 8px',
  borderRadius: '50%',
  transition: 'background 0.2s',
  ':hover': {
      background: 'rgba(255,0,0,0.1)'
  }
});


const tabStyleAdd = (isActive) => ({
  background: isActive ? "#10b981" : "#e2e8f0",
  color: isActive ? "#fff" : "#0f172a",
  border: "none",
  padding: "6px 12px",
  borderRadius: "8px",
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.2s",
});

const label = { fontWeight: 600, color: "#333", marginBottom: "5px" };
const formGroup = { marginBottom: "15px", display: "flex", flexDirection: "column" };

const input = {
  padding: "10px",
  borderRadius: "10px",
  border: "1px solid #ddd",
  outline: "none",
  fontSize: "15px",
};

const dropdown = { ...input, background: "#fff", cursor: "pointer" };
const textarea = { ...input, resize: "none" };
const tagGrid = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  background: "#f8fafc",
  padding: "10px",
  borderRadius: "10px",
};
const tagLabel = {
  background: "#e0e7ff",
  padding: "6px 10px",
  borderRadius: "8px",
  fontSize: "0.85rem",
  color: "#1e3a8a",
  cursor: "pointer",
};

const avatarStyle = {
  width: "100px",
  height: "100px",
  borderRadius: "50%",
  objectFit: "cover",
  border: "3px solid #2563eb",
  marginBottom: "8px",
};

const sectionBox = {
  background: "#f3f4f6",
  padding: "12px",
  borderRadius: "12px",
  textAlign: "center",
  marginBottom: "20px",
};

const fileInput = {
  display: "block",
  margin: "10px auto",
};

const linkStyle = { color: "#2563eb", textDecoration: "none", fontWeight: 600 };

const btnGradient = {
  background: "linear-gradient(45deg,#2563eb,#6366f1)",
  color: "#fff",
  border: "none",
  borderRadius: "12px",
  padding: "10px 20px",
  cursor: "pointer",
  fontWeight: 600,
  width: "100%",
  boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
  marginTop: "10px",
};

const subTitle = {
  color: "#2563eb",
  fontWeight: 700,
  margin: "20px 0 10px",
  textAlign: "center",
};

export default AdminEditProfilePage;